import PortableTextRenderer from "@/app/(site)/_components/Atoms/PortableTextRenderer/PortableTextRenderer"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import { hasVisibleText } from "@/src/utils/a11y/hasVisibleText"
import { headingLevelRank, remapAuthorHeadingLevel } from "@/src/utils/a11y/headingLevel"
import { type HeadingLevel } from "@/types/gc-modules-types"
import type { PortableTextBlock } from "sanity"
import { type ReactNode } from "react"

interface RichTextElementProps {
    richTextGroup?: PortableTextBlock[]
    className?: string
    overrideFont?: string //used in new checkout
    /** Outline level for the first author heading in this block. Default h2. */
    baseHeadingLevel?: HeadingLevel
}

const AUTHOR_HEADINGS = ["h2", "h3", "h4"] as const

function minAuthorHeadingLevel(
    blocks: PortableTextBlock[] | undefined
): HeadingLevel | null {
    if (!blocks?.length) return null

    let min: HeadingLevel | null = null
    for (const block of blocks) {
        const style = "style" in block ? block.style : undefined
        if (style !== "h2" && style !== "h3" && style !== "h4") continue
        if (!min || headingLevelRank(style) < headingLevelRank(min)) min = style
    }

    return min
}

export default function RichTextElement({
    richTextGroup,
    className,
    overrideFont,
    baseHeadingLevel = "h2"
}: RichTextElementProps) {
    const minAuthorLevel = minAuthorHeadingLevel(richTextGroup)

    const headingTag = (
        authorLevel: (typeof AUTHOR_HEADINGS)[number]
    ): "h2" | "h3" | "h4" | "h5" | "h6" => {
        if (!minAuthorLevel) return authorLevel
        const remapped = remapAuthorHeadingLevel(
            authorLevel,
            minAuthorLevel,
            baseHeadingLevel
        )
        return remapped === "h1" ? "h2" : remapped
    }

    const renderHeading = (
        authorLevel: (typeof AUTHOR_HEADINGS)[number],
        fontSize: 24 | 20 | 18,
        text: ReactNode
    ) => {
        if (!hasVisibleText(text)) return null
        return (
            <StoryText
                className={overrideFont}
                GCTag={headingTag(authorLevel)}
                GCFontSize={fontSize}
            >
                {text}
            </StoryText>
        )
    }

    return (
        <div className={className}>
            <PortableTextRenderer
                content={richTextGroup || []}
                render={{
                    block: {
                        normal: text => {
                            return (
                                <StoryText className={overrideFont} GCFontSize={16}>
                                    {text}
                                </StoryText>
                            )
                        },
                        14: text => {
                            return (
                                <StoryText GCFontSize={14} size="size5">
                                    {text}
                                </StoryText>
                            )
                        },
                        16: text => {
                            return (
                                <StoryText className={overrideFont} GCFontSize={16}>
                                    {text}
                                </StoryText>
                            )
                        },
                        18: text => {
                            return (
                                <StoryText className={overrideFont} GCFontSize={18}>
                                    {text}
                                </StoryText>
                            )
                        },
                        h2: text => renderHeading("h2", 24, text),
                        h3: text => renderHeading("h3", 20, text),
                        h4: text => renderHeading("h4", 18, text),
                        quote: text => {
                            return (
                                <StoryText className={overrideFont} GCFontSize={20}>
                                    &ldquo;{text}&rdquo;
                                </StoryText>
                            )
                        }
                    },
                    list: {
                        bullet: text => {
                            return (
                                <StoryText
                                    className={overrideFont}
                                    asChild
                                    GCFontSize={16}
                                >
                                    <ul className="list-disc space-y-2 pl-[1em]">
                                        {text}
                                    </ul>
                                </StoryText>
                            )
                        },
                        number: text => {
                            return (
                                <StoryText
                                    className={overrideFont}
                                    asChild
                                    GCFontSize={16}
                                >
                                    <ol className="list-decimal space-y-2 pl-[1em]">
                                        {text}
                                    </ol>
                                </StoryText>
                            )
                        }
                    }
                }}
            />
        </div>
    )
}
